// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Garantias/GarantiaForm.tsx
//  Criar garantia + ver detalhe + registrar/resolver ocorrências
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Save, ShieldCheck, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useGarantia, useGarantiaDetalhe } from '@/features/garantias/hooks/useGarantia';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import type { StatusGarantia } from '@/features/garantias/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const statusConfig: Record<StatusGarantia, { label: string; className: string }> = {
  vigente:  { label: 'Vigente',  className: 'bg-green-500/10 text-green-600'   },
  acionada: { label: 'Acionada', className: 'bg-orange-500/10 text-orange-600' },
  expirada: { label: 'Expirada', className: 'bg-red-500/10 text-red-600'       },
};

// ── NovaGarantiaForm ──────────────────────────────────────────────────────

function NovaGarantiaForm() {
  const navigate = useNavigate();
  const hoje = new Date().toISOString().slice(0, 10);

  const [clienteId,    setClienteId]    = useState(0);
  const [descricao,    setDescricao]    = useState('');
  const [dtInicio,     setDtInicio]     = useState(hoje);
  const [prazoMeses,   setPrazoMeses]   = useState(12);
  const [numSerie,     setNumSerie]     = useState('');
  const [observacao,   setObservacao]   = useState('');

  const { create, isCreating } = useGarantia();

  const dtVencimentoCalc = () => {
    const d = new Date(dtInicio);
    d.setMonth(d.getMonth() + prazoMeses);
    return d.toLocaleDateString('pt-BR');
  };

  const handleSubmit = async () => {
    if (!clienteId || !descricao) return;
    const g = await create({
      clienteId,
      descricao,
      dtInicio,
      prazoMeses,
      numSerie: numSerie || undefined,
      observacao: observacao || undefined,
    });
    navigate(`/operacional/garantias/${g.id}`);
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/operacional/garantias')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Nova Garantia</h1>
          <p className="text-muted-foreground">Registre a garantia de um produto ou serviço</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Dados da Garantia</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1">
            <Label>Cliente *</Label>
            <PessoaSearchCombobox
              value={clienteId || undefined}
              onChange={(id) => setClienteId(id)}
              filter="cliente"
              placeholder="Buscar cliente..."
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label>Descrição do Produto/Serviço *</Label>
            <Input placeholder="Ex: Notebook Dell Inspiron 15" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Número de Série</Label>
            <Input placeholder="Opcional" value={numSerie} onChange={(e) => setNumSerie(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Data de Início *</Label>
            <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Prazo (meses) *</Label>
            <Input type="number" min={1} max={120} value={prazoMeses}
              onChange={(e) => setPrazoMeses(Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label>Vencimento previsto</Label>
            <Input value={dtVencimentoCalc()} readOnly className="text-muted-foreground bg-muted" />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label>Observações</Label>
            <Textarea rows={3} placeholder="Condições da garantia, exclusões, etc." value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Separator />
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/operacional/garantias')}>Cancelar</Button>
        <Button className="gap-2" onClick={handleSubmit} disabled={isCreating || !clienteId || !descricao}>
          <Save className="h-4 w-4" />{isCreating ? 'Salvando...' : 'Registrar Garantia'}
        </Button>
      </div>
    </div>
  );
}

// ── GarantiaDetalheView ───────────────────────────────────────────────────

function GarantiaDetalheView({ id }: { id: number }) {
  const navigate = useNavigate();
  const { data: g, isLoading, registrarOcorrencia, resolverOcorrencia, isRegistrando } =
    useGarantiaDetalhe(id);
  const { encerrar } = useGarantia();

  const [showOcorrencia,   setShowOcorrencia]   = useState(false);
  const [descOcorrencia,   setDescOcorrencia]   = useState('');
  const [showResolver,     setShowResolver]     = useState(false);
  const [resolvingId,      setResolvingId]      = useState<number | null>(null);
  const [resolucaoTexto,   setResolucaoTexto]   = useState('');

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;
  if (!g)        return <div className="p-6 text-muted-foreground">Garantia não encontrada.</div>;

  const cfg = statusConfig[g.status as StatusGarantia] ?? statusConfig.vigente;

  const handleRegistrar = async () => {
    if (!descOcorrencia.trim()) return;
    await registrarOcorrencia({ descricao: descOcorrencia });
    setDescOcorrencia('');
    setShowOcorrencia(false);
  };

  const handleResolver = async () => {
    if (!resolvingId || !resolucaoTexto.trim()) return;
    await resolverOcorrencia({ oid: resolvingId, req: { resolucao: resolucaoTexto } });
    setResolvingId(null);
    setResolucaoTexto('');
    setShowResolver(false);
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/operacional/garantias')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{g.descricao}</h1>
            <Badge className={cfg.className}>{cfg.label}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            {g.numSerie && `Série: ${g.numSerie} · `}
            Vencimento: {new Date(g.dtVencimento).toLocaleDateString('pt-BR')} ·
            {g.prazoMeses} meses de garantia
          </p>
        </div>
        {g.status !== 'expirada' && g.isAtiva && (
          <Button variant="outline" className="gap-2 text-destructive border-destructive hover:bg-destructive hover:text-white"
            onClick={() => encerrar(g.id)}>
            <XCircle className="h-4 w-4" />Encerrar
          </Button>
        )}
      </div>

      {/* Informações */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Detalhes</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-muted-foreground">Início</p><p className="font-medium">{new Date(g.dtInicio).toLocaleDateString('pt-BR')}</p></div>
          <div><p className="text-muted-foreground">Vencimento</p><p className="font-medium">{new Date(g.dtVencimento).toLocaleDateString('pt-BR')}</p></div>
          <div><p className="text-muted-foreground">Prazo</p><p className="font-medium">{g.prazoMeses} meses</p></div>
          <div><p className="text-muted-foreground">Ocorrências</p><p className="font-medium">{g.ocorrencias.length} ({g.ocorrencias.filter((o) => !o.resolvida).length} abertas)</p></div>
          {g.observacao && <div className="col-span-2 md:col-span-4"><p className="text-muted-foreground">Observação</p><p>{g.observacao}</p></div>}
        </CardContent>
      </Card>

      {/* Ocorrências */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-orange-500" />Ocorrências</CardTitle>
            {g.isAtiva && (
              <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowOcorrencia(true)}>
                <Plus className="h-4 w-4" />Registrar Ocorrência
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {g.ocorrencias.length === 0 ? (
            <p className="text-center py-6 text-sm text-muted-foreground">Nenhuma ocorrência registrada.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Resolução</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.ocorrencias.map((oc) => (
                  <TableRow key={oc.id}>
                    <TableCell>{new Date(oc.dtOcorrencia).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>{oc.descricao}</TableCell>
                    <TableCell>{oc.resolucao || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      {oc.resolvida
                        ? <Badge className="bg-green-500/10 text-green-600"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Resolvida</Badge>
                        : <Badge className="bg-orange-500/10 text-orange-600">Em aberto</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {!oc.resolvida && g.isAtiva && (
                        <Button size="sm" variant="outline" onClick={() => { setResolvingId(oc.id); setShowResolver(true); }}>
                          Resolver
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog: registrar ocorrência */}
      <Dialog open={showOcorrencia} onOpenChange={setShowOcorrencia}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar Ocorrência</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Descrição *</Label>
            <Textarea rows={4} placeholder="Descreva o problema reportado pelo cliente..."
              value={descOcorrencia} onChange={(e) => setDescOcorrencia(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOcorrencia(false)}>Cancelar</Button>
            <Button onClick={handleRegistrar} disabled={isRegistrando || !descOcorrencia.trim()}>
              {isRegistrando ? 'Salvando...' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: resolver ocorrência */}
      <Dialog open={showResolver} onOpenChange={(o) => { if (!o) setResolvingId(null); setShowResolver(o); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolver Ocorrência</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Resolução *</Label>
            <Textarea rows={4} placeholder="Descreva como o problema foi resolvido..."
              value={resolucaoTexto} onChange={(e) => setResolucaoTexto(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolver(false)}>Cancelar</Button>
            <Button onClick={handleResolver} disabled={!resolucaoTexto.trim()}>Marcar como Resolvida</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Roteamento interno ────────────────────────────────────────────────────

const GarantiaForm = () => {
  const { id } = useParams<{ id: string }>();
  const numId = id ? Number(id) : 0;
  if (numId > 0) return <GarantiaDetalheView id={numId} />;
  return <NovaGarantiaForm />;
};

export default GarantiaForm;
